import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Download, Filter } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ExportModal = ({ open, onClose, userRole }) => {
  const [filters, setFilters] = useState({
    date_from: '',
    date_to: '',
    offer: 'all',
    state: 'all',
    agent: 'all',
  });
  const [filterOptions, setFilterOptions] = useState({
    offers: [],
    states: [],
    agents: [],
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      fetchFilterOptions();
    }
  }, [open]);

  const fetchFilterOptions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/records/filters`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setFilterOptions(response.data);
    } catch (error) {
      console.error('Failed to fetch filter options', error);
    }
  };

  const handleExport = async (exportAll = false) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = exportAll ? {} : {
        date_from: filters.date_from || undefined,
        date_to: filters.date_to || undefined,
        offer: filters.offer !== 'all' ? filters.offer : undefined,
        state: filters.state !== 'all' ? filters.state : undefined,
        agent: filters.agent !== 'all' ? filters.agent : undefined,
      };

      const response = await axios.get(`${BACKEND_URL}/api/records/export`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = exportAll 
        ? 'loan_records_all.csv' 
        : `loan_records_filtered_${new Date().toISOString().split('T')[0]}.csv`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(exportAll ? 'All records exported!' : 'Filtered records exported!');
      onClose();
    } catch (error) {
      toast.error('Export failed');
    } finally {
      setLoading(false);
    }
  };

  const resetFilters = () => {
    setFilters({
      date_from: '',
      date_to: '',
      offer: 'all',
      state: 'all',
      agent: 'all',
    });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="export-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold flex items-center gap-2">
            <Download className="w-6 h-6" />
            Export Records
          </DialogTitle>
          <DialogDescription>
            Export all records or apply filters to export specific data
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <Filter className="w-4 h-4" />
            Filter Options
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="date_from">Date From</Label>
              <Input
                id="date_from"
                type="date"
                value={filters.date_from}
                onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                data-testid="filter-date-from"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="date_to">Date To</Label>
              <Input
                id="date_to"
                type="date"
                value={filters.date_to}
                onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                data-testid="filter-date-to"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="offer">Offer Type</Label>
              <Select value={filters.offer} onValueChange={(value) => setFilters({ ...filters, offer: value })}>
                <SelectTrigger data-testid="filter-offer">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Offers</SelectItem>
                  {filterOptions.offers.map((offer) => (
                    <SelectItem key={offer} value={offer}>
                      {offer}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="state">State</Label>
              <Select value={filters.state} onValueChange={(value) => setFilters({ ...filters, state: value })}>
                <SelectTrigger data-testid="filter-state">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All States</SelectItem>
                  {filterOptions.states.map((state) => (
                    <SelectItem key={state} value={state}>
                      {state}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {userRole === 'admin' && (
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="agent">Agent</Label>
                <Select value={filters.agent} onValueChange={(value) => setFilters({ ...filters, agent: value })}>
                  <SelectTrigger data-testid="filter-agent">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Agents</SelectItem>
                    {filterOptions.agents.map((agent) => (
                      <SelectItem key={agent} value={agent}>
                        {agent}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {userRole === 'agent' && filterOptions.agents.length > 0 && (
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="agent">Agent</Label>
                <Select value={filters.agent} onValueChange={(value) => setFilters({ ...filters, agent: value })}>
                  <SelectTrigger data-testid="filter-agent">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Agents</SelectItem>
                    {filterOptions.agents.map((agent) => (
                      <SelectItem key={agent} value={agent}>
                        {agent}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={resetFilters} data-testid="reset-filters">
              Reset Filters
            </Button>
            <p className="text-xs text-muted-foreground">
              Clear all filters to export everything
            </p>
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-3">
          <Button
            variant="outline"
            onClick={() => handleExport(true)}
            disabled={loading}
            className="w-full sm:w-auto"
            data-testid="export-all-button"
          >
            <Download className="mr-2 h-4 w-4" />
            Export All Records
          </Button>
          <Button
            onClick={() => handleExport(false)}
            disabled={loading}
            className="w-full sm:w-auto"
            data-testid="export-filtered-button"
          >
            <Filter className="mr-2 h-4 w-4" />
            {loading ? 'Exporting...' : 'Export with Filters'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ExportModal;